'use client';

import { useState } from 'react';
import SmartLink from '@/server/smart-link/SmartLink';
import type { Translations } from '@/lib/translations/types';

interface PillNavigationProps {
  translation: Translations;
  posts: { id: string; date: string; title: string; slug: string }[];
  categorySlug: string;
}

export default function PillNavigation({ translation, posts, categorySlug }: PillNavigationProps) {
  const [showAll, setShowAll] = useState(false);

  // Show first 5 posts initially, all posts when expanded
  const hasMorePosts = posts.length > 5;
  const initialPosts = posts.slice(0, 5);
  const additionalPosts = posts.slice(5);

  return (
    <div className="space-y-4">
      {/* All pills in one ul */}
      <ul className="flex flex-wrap gap-2" role="list">
        {/* Always show first 5 posts */}
        {initialPosts.map((post) => (
          <li key={post.id}>
            <SmartLink
              href={`/${categorySlug}/${post.slug}/`}
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              title={`${translation.common.articlesAbout} ${post.title}`}
              ariaLabel={`${translation.common.articlesAbout} ${post.title}`}
            >
              {post.title}
            </SmartLink>
          </li>
        ))}
        
        {/* Show additional posts when expanded */}
        {showAll && additionalPosts.map((post) => (
          <li key={post.id}>
            <SmartLink
              href={`/${categorySlug}/${post.slug}/`}
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              title={`${translation.common.articlesAbout} ${post.title}`}
              ariaLabel={`${translation.common.articlesAbout} ${post.title}`}
            >
              {post.title}
            </SmartLink>
          </li>
        ))}
        
        {/* Show All/Show Less Button */}
        {hasMorePosts && (
          <li>
            <button
              onClick={() => setShowAll(!showAll)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-expanded={showAll}
            >
              <span>{showAll ? translation.common.showLess : translation.common.seeAll}</span>
              <svg 
                className={`w-4 h-4 transition-transform duration-200 ${showAll ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}
